import { Routes, Route, Navigate, useLocation, useParams, type Location } from 'react-router-dom'
import WorkspaceLayout from "../../layout/WorkspaceLayout/WorkspaceLayout.tsx"
import ConfirmDialogContainer from "../../ui/ConfirmDialog/ConfirmDialogContainer.tsx"
import ModalRouter from "../../modals/Modal/ModalRouter.tsx"
import OnboardingWorkspaceGate from "../OnboardingWorkspaceGate/OnboardingWorkspaceGate.tsx"
import WorkspaceMyTasksPage from "../../pages/WorkspaceMyTasksPage/WorkspaceMyTasksPage.tsx"
import WorkspaceAllTasksPage from "../../pages/WorkspaceAllTasksPage/WorkspaceAllTasksPage.tsx"
import WorkspaceTeamPage from "../../pages/WorkspaceTeamPage/WorkspaceTeamPage.tsx"
import WorkspaceTeamMemberPage from "../../pages/WorkspaceTeamMemberPage/WorkspaceTeamMemberPage.tsx"
import WorkspaceInboxPage from "../../pages/WorkspaceInboxPage/WorkspaceInboxPage.tsx"
import WorkspaceProjectsPage from "../../pages/WorkspaceProjectsPage/WorkspaceProjectsPage.tsx"
import WorkspaceTaskModalRoute from "../../pages/WorkspaceTaskModalRoute/WorkspaceTaskModalRoute.tsx"
import LegacyTaskRedirect from "../../pages/LegacyTaskRedirect/LegacyTaskRedirect.tsx"
import SuccessPage from "../../pages/SuccessPage/SuccessPage.tsx"
import WelcomeV2 from "../../pages/Welcome/WelcomeV2.tsx"
import AuthPage from "../../pages/AuthPage/AuthPage.tsx"
import NotFoundPage from "../../pages/NotFoundPage/NotFoundPage.tsx"
import WorkspaceNoAccessPage from "../../pages/WorkspaceNoAccessPage/WorkspaceNoAccessPage.tsx"
import { WorkspaceProvider } from "../../../contexts/WorkspaceContext.tsx"
import {useEffect, Suspense} from "react";
import {lazyWithReload} from "@/utils/lazyWithReload";
import {useDispatch} from "react-redux";
import {initAuthSaga} from "../../../store/auth/authSlice.ts";
import type {UserWorkspace} from "../../../store/auth/authSlice.ts";
import {useAppSelector} from "../../../store/hooks.ts";
import GlobalLoadingManager from "../GlobalLoadingManager/GlobalLoadingManager.tsx";
import PageTitle from "../PageTitle/PageTitle.tsx";
import ScrollToTop from "../ScrollToTop/ScrollToTop.tsx";
import Toast from '../Toast/Toast'
import { logTiming } from '@/utils/timing'

// Lazy so the public read-only view (and the Plate viewer it imports directly, to render
// the description without a lazy-pop-in jump) stays out of the main app bundle.
// lazyWithReload recovers from a stale chunk after a deploy (otherwise Suspense fallback={null}
// would leave the route blank forever on a 404'd chunk).
const PublicProjectPage = lazyWithReload(() => import("../../pages/PublicProjectPage/PublicProjectPage.tsx"))
// Internal admin metrics panel (SCD-918). Standalone, email-gated, lazy so it stays out of the
// main app bundle — no regular user ever loads it.
const AdminPage = lazyWithReload(() => import("../../pages/AdminPage/AdminPage.tsx"))
// Eager (not lazy): the heavy Plate editor is now split into its own lazy chunk, loaded only
// on click-to-edit in the description (SCD-874), so this page is tiny and shares Section /
// @dnd-kit with the already-eager My-tasks / All-tasks pages. An eager import removes the
// Suspense blank-frame gap on open, so the project page paints synchronously on navigation
// like those pages — that gap was the residual open delay once the fade was gone.
import WorkspaceProjectPage from "../../pages/WorkspaceProjectPage/WorkspaceProjectPage.tsx"
const LOGIN_SECTION_PATHS: Record<string, string> = {
    'my-tasks': '/my-tasks',
    'team': '/team',
    'all-tasks': '/all-tasks',
    'projects': '/projects',
}

function RootRedirect() {
    const auth = useAppSelector(state => state.auth)
    const globalLoading = useAppSelector(state => state.ui.globalLoading)
    const workspaceUrlId = useAppSelector(state => state.workspace.workspace?.workspaceUrlId)
    const firstProjectId = useAppSelector(state => state.projects.allIds[0])

    if (globalLoading) return null
    if (auth === null) return <WelcomeV2 />
    if (!workspaceUrlId) return null

    const loginSection = auth.loginSection || 'projects'

    if (loginSection === 'projects') {
        return <Navigate to={firstProjectId ? `/${workspaceUrlId}/projects/${firstProjectId}` : `/${workspaceUrlId}/projects`} replace />
    }

    return <Navigate to={`/${workspaceUrlId}${LOGIN_SECTION_PATHS[loginSection]}`} replace />
}

// The bare workspace URL (/:workspace) redirects to the user's default section — like
// RootRedirect does at "/", so landing on a workspace (invite link, switcher, bookmark)
// honors the preference (projects by default) instead of always showing My tasks. My tasks
// now lives at its own /:workspace/my-tasks route.
function WorkspaceIndexRedirect() {
    const { workspace } = useParams()
    const location = useLocation()
    const auth = useAppSelector(state => state.auth)
    const globalLoading = useAppSelector(state => state.ui.globalLoading)
    // Wait for the session to load so we redirect to the user's actual default section,
    // not the 'projects' fallback (which would send a My-tasks-preference user to Projects).
    if (globalLoading || !auth) return null
    const sectionPath = LOGIN_SECTION_PATHS[auth.loginSection || 'projects'] || '/projects'
    // Preserve the query string (e.g. ?settings=integrations from the Slack OAuth return),
    // so a deep-link param survives the bare-workspace → default-section redirect.
    return <Navigate to={`/${workspace}${sectionPath}${location.search}`} replace />
}

// Guards the /:workspace subtree: `/:workspace` matches any first segment, so a
// typed/garbage workspace slug would otherwise render an empty layout. Once the
// session has loaded, if the slug isn't one of the user's workspaces, show 404.
// Gated on a settled session so a valid workspace isn't flashed as 404 during
// bootstrap or a workspace switch.
function WorkspaceRouteGuard() {
    const { workspace } = useParams<{ workspace: string }>()
    const auth = useAppSelector(state => state.auth)
    const globalLoading = useAppSelector(state => state.ui.globalLoading)

    if (
        !globalLoading && auth && auth.workspaces.length > 0 &&
        !auth.workspaces.some((w: UserWorkspace) => w.workspaceUrlId === workspace)
    ) {
        return <NotFoundPage />
    }
    return <WorkspaceLayout />
}

function App() {
    const dispatch = useDispatch()

    const location = useLocation()
    const state = location.state as { backgroundLocation?: Location }
    const isTaskRoute = location.pathname.includes('/task/')
    const isSuccessPage = location.pathname.endsWith('/success')
    // Public read-only project sharing (SCD-541) renders a standalone tree with no
    // auth/workspace bootstrap — visitors aren't signed in and shouldn't trigger it.
    const isPublicShare = location.pathname.startsWith('/share/')
    // Standalone login / registration page — no workspace bootstrap needed.
    const isAuthPage = location.pathname === '/auth'
    // Standalone "you don't have access" screen (SCD-934) — rendered outside WorkspaceProvider
    // so it doesn't try to load the workspace the signed-in user can't read.
    const isNoAccess = location.pathname === '/no-access'
    // Standalone internal admin panel (SCD-918) — email-gated, rendered outside WorkspaceProvider
    // (it aggregates cross-workspace via a callable, loads no single workspace). Keeps the auth
    // bootstrap (needs the signed-in email); the access gate itself is server-side.
    const isAdmin = location.pathname === '/admin'
    const auth = useAppSelector(state => state.auth)
    const globalLoading = useAppSelector(state => state.ui.globalLoading)
    // The landing/marketing page already greets logged-out visitors at the root.
    const isLanding = location.pathname === '/' || location.pathname.startsWith('/about')

    useEffect(() => {
        if (isPublicShare) return
        logTiming('[boot] App mounted, dispatching initAuthSaga')
        dispatch(initAuthSaga())
    }, [dispatch, isPublicShare])


    // Warm the lazily-split editor chunk as soon as the workspace bootstrap is done
    // (global loader gone) for signed-in users, so by the time a project/task is opened
    // its description renders instantly instead of fetching/parsing the chunk mid-open
    // (which janked the modal fade-in and made project descriptions pop in late). We gate
    // on globalLoading rather than requestIdleCallback because, on a busy cold load, idle
    // time can be starved long enough that opening a project beats the prefetch. Gated on
    // a signed-in session so the marketing landing never pulls the heavy editor chunk.
    // Keeps the editor out of the initial bundle (SCD-525); imports are module-cached so
    // the later lazy import resolves immediately.
    const appReady = auth !== null && !globalLoading
    useEffect(() => {
        if (!appReady) return
        // Warm the lazily-split editor chunk so the first click-to-edit on a description (or a
        // task) resolves from cache instead of fetching/parsing mid-edit. The project page itself
        // is eager now, and the read view (TextStatic) is a tiny serializer in the main bundle.
        import('../../app/TextEditor/TextEditor')
    }, [appReady])

    if (isPublicShare) {
        // No WorkspaceProvider / loader / auth — the page fetches everything it needs
        // from the unauthenticated public endpoint. The page renders its own task overlay.
        return (
            <>
                <ScrollToTop />
                <Suspense fallback={null}>
                    <Routes>
                        <Route path="/share/:shareId/*" element={<PublicProjectPage />} />
                    </Routes>
                </Suspense>
            </>
        )
    }

    if (isAuthPage) {
        // Standalone /auth login page — rendered outside WorkspaceProvider.
        return <AuthPage />
    }

    if (isNoAccess) {
        // Reached only via loadUserData's redirect for a link to a workspace the signed-in
        // user isn't a member of. Wait for the session so it can show who's signed in.
        if (globalLoading) return null
        return <WorkspaceNoAccessPage />
    }

    if (isAdmin) {
        // Render as soon as the session resolves to a signed-in user — do NOT wait on
        // `globalLoading`, which only clears after the projects snapshot forked by
        // WorkspaceProvider (never mounted here), so on /admin it only clears via the ~8s
        // watchdog. `auth` is a real user object once loadUserData dispatches setAuth.
        // Whether that user is actually an admin is decided server-side by getAdminMetrics
        // (the page shows a not-authorized state on permission-denied). AdminPage lifts the
        // index.html splash itself, since GlobalLoadingManager isn't mounted outside
        // WorkspaceProvider.
        if (auth) {
            return (
                <Suspense fallback={null}>
                    <AdminPage />
                </Suspense>
            )
        }
        // Session not resolved yet → keep the splash; once settled to logged-out, go to /auth.
        if (globalLoading) return null
        return <Navigate to="/auth" state={{ next: '/admin' }} replace />
    }

    // A logged-out visitor opening a task/project link is sent to /auth, remembering
    // where they were headed so signing in returns them there. globalLoading is still
    // true until bootstrap resolves the session, so this can't fire for a signed-in
    // user.
    if (auth === null && !globalLoading && !isLanding) {
        return <Navigate to="/auth" state={{ next: location.pathname + location.search }} replace />
    }

    return (
        <WorkspaceProvider>
            <ScrollToTop />
            <Routes location={state?.backgroundLocation ?? location}>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/about" element={<WelcomeV2 />} />
                <Route path="/:workspace" element={<WorkspaceRouteGuard />}>
                    <Route index element={<WorkspaceIndexRedirect />} />
                    <Route path="my-tasks" element={<WorkspaceMyTasksPage />} />
                    <Route path="all-tasks" element={<WorkspaceAllTasksPage />} />
                    <Route path="team" element={<WorkspaceTeamPage />} />
                    <Route path="team/:userId" element={<WorkspaceTeamMemberPage />} />
                    <Route path="inbox" element={<WorkspaceInboxPage />} />
                    <Route path="projects" element={<WorkspaceProjectsPage />} />
                    <Route path="projects/:projectId" element={<WorkspaceProjectPage />} />
                    {/* Back-compat: old email task links (Firestore id) → canonical PREFIX-NUMBER modal (SCD-563) */}
                    <Route path="projects/:projectId/task/:taskId" element={<LegacyTaskRedirect />} />
                    {/* The task modal renders in the separate Routes block below; on a direct
                        task link (no backgroundLocation) this matches so the 404 catch-all
                        doesn't render behind the modal. */}
                    <Route path="task/:taskId" element={<></>} />
                    {/* Unknown sub-path under a valid workspace → 404 (rendered in the layout) */}
                    <Route path="*" element={<NotFoundPage />} />
                </Route>
                <Route path="/:workspace/success" element={<SuccessPage />} />
                {/* Anything else → 404 */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>

            {(state?.backgroundLocation || isTaskRoute) && (
                <Routes>
                    <Route
                        path="/:workspace/task/:taskId"
                        element={<WorkspaceTaskModalRoute />}
                    />
                </Routes>
            )}

            {!isSuccessPage && <ModalRouter />}
            {!isSuccessPage && !isPublicShare && <OnboardingWorkspaceGate />}
            <ConfirmDialogContainer />
            <GlobalLoadingManager />
            <PageTitle />
            <Toast />
        </WorkspaceProvider>
    )
}

export default App
